import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ContactFormDialog from "@/components/customers/ContactFormDialog";
import { useCustomerContacts } from "@/hooks/use-customer-contacts";
import { contactLabel } from "@/lib/contact-display";
import type { DBCustomerContact } from "@/lib/db-types";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatPhoneDisplay } from "@/lib/phone-format";
import { cn } from "@/lib/utils";

export type PhoneOutcome = "meeting" | "email" | "call_again" | "no_answer" | "not_relevant";

const OUTCOMES: ReadonlyArray<{ outcome: PhoneOutcome; label: string; hint: string; icon?: string }> = [
  { outcome: "meeting", label: "Booket møte", hint: "Velg tid, invitasjon sendes", icon: "calendar" },
  { outcome: "email", label: "Følg opp på e-post", hint: "Utkast fra notatet", icon: "mail" },
  { outcome: "call_again", label: "Ring igjen", hint: "Velg dato" },
  { outcome: "no_answer", label: "Fikk ikke svar", hint: "Ny samtale i morgen" },
  { outcome: "not_relevant", label: "Ikke aktuelt nå", hint: "Lukk med grunn", icon: "x-circle" },
];

interface PhoneNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  /** Kontakten oppfølgingen gjelder; forhåndsvalgt når den har telefon. */
  defaultContactId?: string | null;
  /** Kundens telefon fra PowerOffice, når ingen kontakt har nummer. */
  customerPhone?: string | null;
  /** Notat som ble skrevet før flyten ble avbrutt. */
  defaultNote?: string;
  pending?: boolean;
  /** `contactId` er kontakten som ble ringt, eller null når kundens nummer ble brukt. */
  onOutcome: (outcome: PhoneOutcome, note: string | null, contactId: string | null) => void;
}

const CUSTOMER_PHONE = "customer";

/**
 * Samtalen (VIB-2958): nummeret øverst, notat, og «Hva ble avtalt?» som lagrer
 * notatet og går rett til neste handling. Hvem du ringer kan byttes, og en ny
 * eller rettet kontakt lagres rett herfra (VIB-2986).
 */
export default function PhoneNoteDialog({ open, onOpenChange, customerId, customerName, defaultContactId, customerPhone, defaultNote = "", pending = false, onOutcome }: PhoneNoteDialogProps) {
  const { data: contacts = [] } = useCustomerContacts(customerId);
  const callable = useMemo(() => contacts.filter((c) => c.is_active && !c.anonymized_at && !!c.phone), [contacts]);
  const [note, setNote] = useState("");
  const [calleeId, setCalleeId] = useState("");
  const [contactForm, setContactForm] = useState<"new" | DBCustomerContact | null>(null);
  useEffect(() => {
    if (open) setNote(defaultNote);
    else setCalleeId("");
  }, [open, defaultNote]);
  // Standard: oppfølgingens kontakt, ellers hovedkontakten, ellers kundens nummer.
  useEffect(() => {
    if (!open || calleeId) return;
    const preferred = callable.find((c) => c.id === defaultContactId) ?? callable.find((c) => c.is_primary) ?? callable[0];
    if (preferred) setCalleeId(preferred.id);
    else if (customerPhone) setCalleeId(CUSTOMER_PHONE);
  }, [open, calleeId, callable, defaultContactId, customerPhone]);

  const callee = callable.find((c) => c.id === calleeId) ?? null;
  const rawPhone = callee?.phone ?? (calleeId === CUSTOMER_PHONE ? customerPhone : null) ?? null;
  const phone = rawPhone ? formatPhoneDisplay(rawPhone) : null;
  const label = (c: DBCustomerContact) => {
    const l = contactLabel({ name: c.full_name, title: c.title, phone: c.phone });
    return l ? [l.primary, l.secondary].filter(Boolean).join(", ") : formatPhoneDisplay(c.phone);
  };
  const choose = (outcome: PhoneOutcome) => onOutcome(outcome, note.trim() || null, callee?.id ?? null);
  const copy = async () => {
    if (!phone) return;
    try {
      await navigator.clipboard.writeText(phone);
      toast.success("Nummeret er kopiert");
    } catch {
      toast.error("Kunne ikke kopiere nummeret");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Samtale med {customerName}</DialogTitle>
          <DialogDescription>Notatet lagres i kontaktloggen.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 bg-muted px-3.5 py-3">
          {(callable.length > 0 || customerPhone) && (
            <Select value={calleeId} onValueChange={setCalleeId}>
              <SelectTrigger aria-label="Hvem ringer du?" className="h-9 bg-card text-sm">
                <SelectValue placeholder="Velg kontakt" />
              </SelectTrigger>
              <SelectContent>
                {callable.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {label(c)}
                  </SelectItem>
                ))}
                {customerPhone && <SelectItem value={CUSTOMER_PHONE}>{customerName} (sentralbord)</SelectItem>}
              </SelectContent>
            </Select>
          )}
          <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            {phone ? (
              <a href={`tel:${rawPhone}`} className="font-display text-2xl font-semibold tabular-nums tracking-wide">
                {phone}
              </a>
            ) : (
              <p className="text-sm text-muted-foreground">Ingen telefon registrert</p>
            )}
          </div>
          {phone && (
            <Button type="button" variant="outline" size="sm" onClick={copy}>
              Kopier
            </Button>
          )}
          </div>
          <div className="flex gap-3 text-sm">
            <button type="button" className="font-medium underline-offset-4 hover:underline" onClick={() => setContactForm("new")}>
              Ny kontaktperson
            </button>
            {callee && (
              <button type="button" className="font-medium underline-offset-4 hover:underline" onClick={() => setContactForm(callee)}>
                Rediger kontakt
              </button>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="phone-note">Hva ble sagt?</Label>
          <Textarea id="phone-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4} autoFocus placeholder="Kort om samtalen" />
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-medium" id="phone-outcome-label">
            Hva ble avtalt?
          </p>
          <div role="group" aria-labelledby="phone-outcome-label" className="grid gap-2 sm:grid-cols-2">
            {OUTCOMES.map((o) => (
              <button
                key={o.outcome}
                type="button"
                disabled={pending}
                onClick={() => choose(o.outcome)}
                className={cn(
                  "flex items-center gap-3 rounded-lg border-[1.5px] border-border bg-card px-3 py-2.5 text-left transition-colors",
                  "hover:border-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50",
                )}
              >
                {o.icon && <SrIcon name={o.icon} className="h-[18px] w-[18px] shrink-0" />}
                <span className="flex flex-col">
                  <span className="text-sm font-medium">{o.label}</span>
                  <span className="text-xs text-muted-foreground">{o.hint}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
        </DialogFooter>
        <ContactFormDialog
          open={contactForm !== null}
          onOpenChange={(o) => !o && setContactForm(null)}
          customerId={customerId}
          contact={contactForm === "new" ? null : contactForm}
          onSaved={(c) => c.phone && setCalleeId(c.id)}
        />
      </DialogContent>
    </Dialog>
  );
}
